import { Box, Grid, InputBase, Paper, Typography } from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { __type_builder } from "../../../../../utils/type-builder";
import ErrorHelper from "../../../../ErrorWrapper";

const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto, Helvetica Neue, Arial, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const CARD_BRAND_ICONS: Record<string, string> = {
  visa: "https://img.icons8.com/color/48/000000/visa.png",
  mastercard: "https://img.icons8.com/color/48/000000/mastercard-logo.png",
  amex: "https://img.icons8.com/color/48/000000/amex.png",
  diners: "https://img.icons8.com/color/48/000000/diners-club.png",
  discover: "https://img.icons8.com/color/48/000000/discover.png",
  jcb: "https://img.icons8.com/color/48/000000/jcb.png",
  unionpay: "https://img.icons8.com/color/48/000000/unionpay.png",
  rupay: "https://seeklogo.com/images/R/rupay-logo-CA2C0C6DCB-seeklogo.com.png",
  unknown:
    "https://upload.wikimedia.org/wikipedia/commons/8/89/HD_transparent_picture.png",
};

// All four card fields are mandatory, so the required marker is rendered
// through one label rather than repeated inline per field.
const RequiredLabel = ({ label }: { label: string }) => (
  <Typography
    sx={{
      fontWeight: 500,
      mb: 0.5,
    }}
  >
    {label}
    <Typography component="span" color="error">
      {" *"}
    </Typography>
  </Typography>
);

interface CustomCardElementProps {
  error?: string;
  cardHolderName?: string;
  cardHolderError?: string;
  onCardHolderChange: (name: string) => void;
}

const CustomCardElement = ({
  error,
  cardHolderName = "",
  cardHolderError,
  onCardHolderChange,
}: CustomCardElementProps) => {
  const [brand, setBrand] = useState<string>("unknown");
  const initialCardError = { cardNumber: "", expiry: "", cvc: "" };
  type CardError = __type_builder<typeof initialCardError>;
  const [cardError, setCardError] = useState<CardError>(initialCardError);

  const handleCardErrors = (k: keyof CardError, err?: string) => {
    setCardError((prev) => ({
      ...prev,
      [k]: err ?? "",
    }));
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        width: "100%",
        maxWidth: 500,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <ErrorHelper error={cardError.cardNumber}>
            <RequiredLabel label="Card Number (xxxx xxxx xxxx 1234)" />
            <Paper
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 1,
                boxShadow: "none",
                borderColor: "#e0e0e0",
                px: 1.5,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <CardNumberElement
                  options={{
                    ...ELEMENT_OPTIONS,
                    placeholder: "****  ****  ****  9090",
                  }}
                  onChange={(event) => {
                    handleCardErrors("cardNumber", event?.error?.message);
                    setBrand(event.brand || "unknown");
                  }}
                />
              </Box>
              <Box
                sx={{
                  ml: 1,
                }}
              >
                <img
                  src={CARD_BRAND_ICONS[brand] || CARD_BRAND_ICONS.unknown}
                  alt={brand}
                  style={{ height: 28, width: "auto" }}
                />
              </Box>
            </Paper>
          </ErrorHelper>
        </Grid>
        <Grid size={12}>
          {/* <CardHolderNameElement /> */}
          <ErrorHelper error={cardHolderError}>
            <RequiredLabel label="Card Holder Name" />
            <InputBase
              fullWidth
              value={cardHolderName}
              onChange={(e) => onCardHolderChange(e.target.value)}
              placeholder="Nicholas Mark Parker"
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                px: 1.5,
                py: 1,
                fontSize: 16,
                background: "#fafbfc",
              }}
              inputProps={{ "aria-label": "Card Holder Name" }}
            />
          </ErrorHelper>
        </Grid>
        <Grid size={6}>
          <ErrorHelper error={cardError.expiry}>
            <RequiredLabel label="Expiry" />
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1,
                boxShadow: "none",
                borderColor: "#e0e0e0",
                px: 1.5,
                py: 0.5,
              }}
            >
              <CardExpiryElement
                options={ELEMENT_OPTIONS}
                onChange={(event) => {
                  handleCardErrors("expiry", event?.error?.message);
                }}
              />
            </Paper>
          </ErrorHelper>
        </Grid>

        <Grid size={6}>
          <ErrorHelper error={cardError.cvc}>
            <RequiredLabel label="CVC" />
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1,
                boxShadow: "none",
                borderColor: "#e0e0e0",
                px: 1.5,
                py: 0.5,
              }}
            >
              <CardCvcElement
                options={ELEMENT_OPTIONS}
                onChange={(event) => {
                  handleCardErrors("cvc", event?.error?.message);
                }}
              />
            </Paper>
          </ErrorHelper>
        </Grid>

        {error && (
          <Grid size={12}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CustomCardElement;
